import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { ArrowLeft, Check, X, User, Clock, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const QuizGrading = () => {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [grades, setGrades] = useState({});
  const [feedbackGeneral, setFeedbackGeneral] = useState('');

  useEffect(() => {
    fetchAttempt();
  }, [attemptId]);

  const fetchAttempt = async () => {
    try {
      const response = await axios.get(`${API_URL}/quiz-attempts/${attemptId}`);
      setAttempt(response.data);
      
      // Inicializar grades con valores existentes
      const initialGrades = {};
      response.data.questions?.forEach(q => {
        if (q.respuesta_alumno) {
          initialGrades[q.id] = {
            puntos: q.respuesta_alumno.puntos_obtenidos || 0,
            feedback: q.respuesta_alumno.feedback || ''
          };
        }
      });
      setGrades(initialGrades);
      setFeedbackGeneral(response.data.feedback_instructor || '');
    } catch (error) {
      toast.error('Error al cargar el intento');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const responses = Object.entries(grades).map(([questionId, data]) => ({
        question_id: questionId,
        puntos: data.puntos,
        feedback: data.feedback
      }));

      await axios.post(`${API_URL}/quiz-attempts/${attemptId}/grade`, {
        responses,
        feedback_general: feedbackGeneral
      });

      toast.success('Calificación guardada');
      fetchAttempt();
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const updateGrade = (questionId, field, value) => {
    setGrades(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [field]: value
      }
    }));
  };

  const getStatusBadge = (status) => {
    const badges = {
      en_progreso: { label: 'En progreso', color: 'bg-yellow-100 text-yellow-800' },
      completado: { label: 'Completado', color: 'bg-blue-100 text-blue-800' },
      calificado: { label: 'Calificado', color: 'bg-green-100 text-green-800' }
    };
    const badge = badges[status] || badges.en_progreso;
    return <Badge className={badge.color}>{badge.label}</Badge>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004E89]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Volver
            </Button>
            <div>
              <h1 className="font-manrope font-bold text-2xl text-slate-900">Calificar Quiz</h1>
              <p className="text-slate-500">{attempt?.quiz?.titulo}</p>
            </div>
          </div>
          {getStatusBadge(attempt?.status)}
        </div>

        {/* Student Info */}
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
              <User className="w-6 h-6 text-slate-400" />
            </div>
            <div>
              <p className="font-medium text-slate-900">
                {attempt?.student?.nombre} {attempt?.student?.apellidos}
              </p>
              <p className="text-sm text-slate-500">{attempt?.student?.email}</p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-2xl font-bold text-slate-900">{attempt?.porcentaje}%</p>
              <p className="text-sm text-slate-500">{attempt?.puntuacion} / {attempt?.puntuacion_maxima} puntos</p>
            </div>
          </div>
        </div>

        {/* Questions */}
        <div className="space-y-4">
          {attempt?.questions?.map((question, index) => {
            const userAnswer = question.respuesta_alumno;
            const isAutoGraded = question.tipo !== 'short_answer';
            const grade = grades[question.id] || { puntos: 0, feedback: '' };
            
            return (
              <div key={question.id} className="bg-white rounded-lg border border-slate-200 p-6">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#004E89] text-white flex items-center justify-center text-sm font-medium flex-shrink-0">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-slate-900 mb-2">{question.texto}</p>
                    <p className="text-sm text-slate-500 mb-4">
                      Tipo: {question.tipo} • Máx: {question.puntos} punto{question.puntos !== 1 ? 's' : ''}
                    </p>

                    {/* User's answer */}
                    <div className="bg-slate-50 rounded-lg p-4 mb-4">
                      <p className="text-sm text-slate-500 mb-1">Respuesta del alumno:</p>
                      {question.tipo === 'multiple_answer' ? (
                        <p className="text-slate-900">
                          {userAnswer?.respuestas_multiples?.map(id => {
                            const opt = question.opciones?.find(o => o.id === id);
                            return opt?.texto;
                          }).join(', ') || 'Sin respuesta'}
                        </p>
                      ) : question.tipo === 'multiple_choice' ? (
                        <p className="text-slate-900">
                          {question.opciones?.find(o => o.id === userAnswer?.respuesta)?.texto || 'Sin respuesta'}
                        </p>
                      ) : (
                        <p className="text-slate-900 whitespace-pre-wrap">
                          {userAnswer?.respuesta || 'Sin respuesta'}
                        </p>
                      )}
                    </div>

                    {/* Correct answer for reference */}
                    {isAutoGraded && (
                      <div className="bg-green-50 rounded-lg p-4 mb-4">
                        <p className="text-sm text-green-600 mb-1">Respuesta correcta:</p>
                        <p className="text-green-800">
                          {question.tipo === 'multiple_answer'
                            ? question.opciones?.filter(o => o.es_correcta).map(o => o.texto).join(', ')
                            : question.respuesta_correcta || question.opciones?.find(o => o.es_correcta)?.texto
                          }
                        </p>
                      </div>
                    )}

                    {/* Grading inputs */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Puntos otorgados</Label>
                        <Input
                          type="number"
                          min="0"
                          max={question.puntos}
                          step="0.5"
                          value={grade.puntos}
                          onChange={(e) => updateGrade(question.id, 'puntos', parseFloat(e.target.value) || 0)}
                          disabled={isAutoGraded}
                        />
                      </div>
                      <div>
                        <Label>Feedback (opcional)</Label>
                        <Input
                          value={grade.feedback}
                          onChange={(e) => updateGrade(question.id, 'feedback', e.target.value)}
                          placeholder="Comentario para el alumno..."
                        />
                      </div>
                    </div>

                    {/* Auto-graded indicator */}
                    {isAutoGraded && (
                      <div className="mt-2 flex items-center gap-2 text-sm">
                        {userAnswer?.es_correcta ? (
                          <span className="text-green-600 flex items-center gap-1"><Check className="w-4 h-4" /> Correcta (automático)</span>
                        ) : (
                          <span className="text-red-600 flex items-center gap-1"><X className="w-4 h-4" /> Incorrecta (automático)</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* General Feedback */}
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <Label>Comentarios generales para el alumno</Label>
          <Textarea
            value={feedbackGeneral}
            onChange={(e) => setFeedbackGeneral(e.target.value)}
            placeholder="Escribe un comentario general sobre el desempeño del alumno..."
            rows={3}
            className="mt-2"
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <Button variant="outline" onClick={() => navigate(-1)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#004E89] hover:bg-[#003B66]">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Guardando...' : 'Guardar Calificación'}
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default QuizGrading;
